import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  AdminAudienceState,
  ProjectorBoardSort,
  ProjectorState,
} from "../../shared/types.ts";
import type { ThreadActions } from "../components/ThreadRow.tsx";
import type { RoleAuth } from "./auth.ts";
import {
  applyOptimisticAudienceActions,
  collectPendingAudienceIds,
  isOptimisticActionSatisfied,
  type AudienceOptimisticAction,
  type PendingAudienceIds,
} from "./optimisticAudience.ts";
import {
  notificationCopy,
  projectedRepliesVisibilityCopy,
  projectionBoardShownCopy,
  projectionJoinQrShownCopy,
  projectionRemovedCopy,
  projectionThreadShownCopy,
  replyStatusCopy,
  threadEditSavedCopy,
  threadStatusCopy,
  type NotificationMessage,
  type ProjectionToastKind,
} from "./notificationCopy.ts";
import { notify } from "./notify.ts";
import {
  requireMutationResult,
  type StaffActionToastCopy,
} from "./staffNotifications.ts";

type PendingOptimisticAction = AudienceOptimisticAction extends infer Action
  ? Action extends { id: string }
    ? Omit<Action, "id">
    : never
  : never;

export interface AdminProjectorActions {
  showBoard: (boardSort: ProjectorBoardSort) => void;
  showJoinQr: () => void;
  showKeyThemes: () => void;
  showKeyThemesBoard: () => void;
  setRepliesVisible: (visible: boolean) => void;
}

export interface AdminOptimisticAudienceResult {
  state: AdminAudienceState | null | undefined;
  threadActions: ThreadActions;
  projectorActions: AdminProjectorActions;
  pending: PendingAudienceIds;
}

export function useOptimisticAudienceState(
  auth: RoleAuth,
  audience: AdminAudienceState | null | undefined,
): AdminOptimisticAudienceResult {
  const setPostStatus = useMutation(convexApi.audience.setPostStatus);
  const editPost = useMutation(convexApi.audience.editPost);
  const setReplyStatus = useMutation(convexApi.audience.setReplyStatus);
  const setPostCategory = useMutation(convexApi.categories.setPostCategory);
  const recategorizePost = useMutation(convexApi.audience.recategorizePost);
  const projectThread = useMutation(convexApi.projector.projectThread);
  const showDefault = useMutation(convexApi.projector.showDefault);
  const showBoardMutation = useMutation(convexApi.projector.showBoard);
  const showJoinQrMutation = useMutation(convexApi.projector.showJoinQr);
  const showKeyThemesMutation = useMutation(convexApi.projector.showKeyThemes);
  const showKeyThemesBoardMutation = useMutation(convexApi.projector.showKeyThemesBoard);
  const setProjectedRepliesVisible = useMutation(convexApi.projector.setProjectedRepliesVisible);
  const [pendingOps, setPendingOps] = useState<AudienceOptimisticAction[]>([]);
  const nextOpId = useRef(0);
  const sessionArg = audience?.sessionId ? audience.sessionId as Id<"sessions"> : null;

  useEffect(() => {
    if (!audience) return;
    setPendingOps((prev) => prev.filter((op) => !isOptimisticActionSatisfied(audience, op)));
  }, [audience]);

  const optimisticAudience = useMemo(
    () => (audience ? applyOptimisticAudienceActions(audience, pendingOps) : audience),
    [audience, pendingOps],
  );
  const pending = useMemo(() => collectPendingAudienceIds(pendingOps), [pendingOps]);

  function staffCopy(success: NotificationMessage, error: NotificationMessage): StaffActionToastCopy {
    return { success, error };
  }

  function boardSortLabel(sort: ProjectorBoardSort): string {
    switch (sort) {
      case "most_liked":
        return "most upvotes";
      case "most_replied":
        return "most replies";
      case "oldest_unanswered":
      default:
        return "oldest first";
    }
  }

  function findReplyBody(replyId: string): string | undefined {
    for (const thread of audience?.threads ?? []) {
      const reply = (thread.replies ?? []).find((entry) => entry.id === replyId);
      if (reply) return reply.body;
    }
    return undefined;
  }

  function projectedThreadBody(projector: ProjectorState | null | undefined = audience?.projector): string | undefined {
    if (projector?.mode !== "thread" || !projector.projectedThreadId) return undefined;
    return audience?.threads.find((thread) => thread.id === projector.projectedThreadId)?.body;
  }

  function projectedArtifactBody(projector: ProjectorState | null | undefined = audience?.projector): string | undefined {
    if (projector?.mode !== "artifact" || !projector.projectedArtifactId) return undefined;
    return audience?.artifacts.find((artifact) => artifact.id === projector.projectedArtifactId)?.body;
  }

  function projectionKind(projector: ProjectorState | null | undefined): ProjectionToastKind {
    if (projector?.mode === "thread") return "thread";
    if (projector?.mode === "join-qr") return "join-qr";
    if (projector?.mode === "artifact") return "artifact";
    return "board";
  }

  function projectionDetail(projector: ProjectorState | null | undefined): string | undefined {
    if (projector?.mode === "thread") return projectedThreadBody(projector);
    if (projector?.mode === "artifact") return projectedArtifactBody(projector);
    if (projector?.mode === "board") return `Board sort: ${boardSortLabel(projector.boardSort)}`;
    return undefined;
  }

  function begin(op: PendingOptimisticAction, run: () => Promise<unknown>, copy: StaffActionToastCopy) {
    beginMany([op], run, copy);
  }

  function beginMany(ops: PendingOptimisticAction[], run: () => Promise<unknown>, copy: StaffActionToastCopy) {
    const fullOps = ops.map((op) => ({
      ...op,
      id: `optimistic-${nextOpId.current++}`,
    })) as AudienceOptimisticAction[];
    const ids = new Set(fullOps.map((op) => op.id));

    setPendingOps((prev) => [...prev, ...fullOps]);
    void run()
      .then(() => notify.success(copy.success))
      .catch(() => {
        setPendingOps((prev) => prev.filter((entry) => !ids.has(entry.id)));
        notify.error(copy.error);
      });
  }

  const threadActions: ThreadActions = {
    approve: (thread) => begin(
      { kind: "postStatus", postId: thread.id, status: "shown", now: Date.now() },
      async () => {
        await requireMutationResult(setPostStatus({ auth, postId: thread.id as Id<"audiencePosts">, status: "shown" }));
      },
      staffCopy(threadStatusCopy("approved", thread.body), notificationCopy.staff.couldNotUpdateStatus),
    ),
    answer: (thread) => begin(
      { kind: "postStatus", postId: thread.id, status: "answered", now: Date.now() },
      async () => {
        await requireMutationResult(setPostStatus({ auth, postId: thread.id as Id<"audiencePosts">, status: "answered" }));
      },
      staffCopy(threadStatusCopy("answered", thread.body), notificationCopy.staff.couldNotUpdateStatus),
    ),
    reopen: (thread) => begin(
      { kind: "postStatus", postId: thread.id, status: "shown", now: Date.now() },
      async () => {
        await requireMutationResult(setPostStatus({ auth, postId: thread.id as Id<"audiencePosts">, status: "shown" }));
      },
      staffCopy(threadStatusCopy("reopened", thread.body), notificationCopy.staff.couldNotUpdateStatus),
    ),
    hide: (thread) => {
      const now = Date.now();
      const isProjected = audience?.projector?.mode === "thread"
        && audience.projector.projectedThreadId === thread.id
        && sessionArg !== null;
      const ops: PendingOptimisticAction[] = [
        { kind: "postStatus", postId: thread.id, status: "hidden", now },
      ];
      if (isProjected && audience) {
        ops.push({ kind: "showDefault", sessionId: audience.sessionId, now });
      }

      beginMany(ops, async () => {
        await requireMutationResult(setPostStatus({ auth, postId: thread.id as Id<"audiencePosts">, status: "hidden" }));
        if (isProjected && sessionArg) {
          await requireMutationResult(showDefault({ auth, sessionId: sessionArg }));
        }
      }, staffCopy(threadStatusCopy("hidden", thread.body), notificationCopy.staff.couldNotUpdateStatus));
    },
    saveEdit: (thread, body) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      begin(
        { kind: "editPost", postId: thread.id, body: trimmed, now: Date.now() },
        async () => {
          await requireMutationResult(editPost({ auth, postId: thread.id as Id<"audiencePosts">, body: trimmed }));
        },
        staffCopy(threadEditSavedCopy(trimmed), notificationCopy.staff.couldNotUpdateText),
      );
    },
    approveReply: (replyId) => begin(
      { kind: "replyStatus", replyId, status: "shown", now: Date.now() },
      async () => {
        await requireMutationResult(setReplyStatus({ auth, replyId: replyId as Id<"audienceReplies">, status: "shown" }));
      },
      staffCopy(replyStatusCopy("approved", findReplyBody(replyId)), notificationCopy.staff.couldNotUpdateReply),
    ),
    hideReply: (replyId) => begin(
      { kind: "replyStatus", replyId, status: "hidden", now: Date.now() },
      async () => {
        await requireMutationResult(setReplyStatus({ auth, replyId: replyId as Id<"audienceReplies">, status: "hidden" }));
      },
      staffCopy(replyStatusCopy("hidden", findReplyBody(replyId)), notificationCopy.staff.couldNotUpdateReply),
    ),
    project: (thread) => begin(
      { kind: "projectThread", threadId: thread.id, showReplies: true, now: Date.now() },
      async () => {
        await requireMutationResult(projectThread({ auth, threadId: thread.id as Id<"audiencePosts">, showReplies: true }));
      },
      staffCopy(projectionThreadShownCopy(thread.body, true), notificationCopy.staff.couldNotUpdateScreen),
    ),
    stopProjection: () => {
      if (!audience || !sessionArg) return;
      const currentProjector = audience.projector;
      begin(
        { kind: "showDefault", sessionId: audience.sessionId, now: Date.now() },
        async () => {
          await requireMutationResult(showDefault({ auth, sessionId: sessionArg }));
        },
        staffCopy(
          projectionRemovedCopy(projectionKind(currentProjector), projectionDetail(currentProjector)),
          notificationCopy.staff.couldNotUpdateScreen,
        ),
      );
    },
    // Category changes render via the live subscription quickly and have no
    // optimistic-op kind — plain fire-with-toast is enough here.
    setCategory: (thread, categoryId) => {
      void requireMutationResult(setPostCategory({
        auth,
        postId: thread.id as Id<"audiencePosts">,
        categoryId: categoryId as Id<"audienceCategories">,
      }))
        .then(() => notify.success({ title: "Category updated" }))
        .catch(() => notify.error({ title: "Could not update the category" }));
    },
    recategorize: (thread) => {
      void requireMutationResult(recategorizePost({
        auth,
        postId: thread.id as Id<"audiencePosts">,
      }))
        .then(() => notify.success({ title: "Re-running AI categorization" }))
        .catch(() => notify.error({ title: "Could not re-run AI categorization" }));
    },
  };

  const projectorActions: AdminProjectorActions = {
    showBoard: (boardSort) => {
      if (!audience || !sessionArg) return;
      begin(
        { kind: "showBoard", sessionId: audience.sessionId, boardSort, now: Date.now() },
        async () => {
          await requireMutationResult(showBoardMutation({ auth, sessionId: sessionArg, boardSort }));
        },
        staffCopy(projectionBoardShownCopy(boardSortLabel(boardSort)), notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    showJoinQr: () => {
      if (!audience || !sessionArg) return;
      begin(
        { kind: "showJoinQr", sessionId: audience.sessionId, now: Date.now() },
        async () => {
          await requireMutationResult(showJoinQrMutation({ auth, sessionId: sessionArg }));
        },
        staffCopy(projectionJoinQrShownCopy(audience.joinCode), notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    showKeyThemes: () => {
      if (!audience || !sessionArg) return;
      begin(
        { kind: "showKeyThemes", sessionId: audience.sessionId, now: Date.now() },
        async () => {
          await requireMutationResult(showKeyThemesMutation({ auth, sessionId: sessionArg }));
        },
        staffCopy({ title: "Discussion + key themes on screen" }, notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    showKeyThemesBoard: () => {
      if (!audience || !sessionArg) return;
      begin(
        { kind: "showKeyThemesBoard", sessionId: audience.sessionId, now: Date.now() },
        async () => {
          await requireMutationResult(showKeyThemesBoardMutation({ auth, sessionId: sessionArg }));
        },
        staffCopy({ title: "Key themes board on screen" }, notificationCopy.staff.couldNotUpdateScreen),
      );
    },
    setRepliesVisible: (visible) => {
      if (!audience || !sessionArg) return;
      const threadBody = projectedThreadBody();
      begin(
        { kind: "setRepliesVisible", sessionId: audience.sessionId, visible, now: Date.now() },
        async () => {
          await requireMutationResult(setProjectedRepliesVisible({ auth, sessionId: sessionArg, visible }));
        },
        staffCopy(projectedRepliesVisibilityCopy(visible, threadBody), notificationCopy.staff.couldNotUpdateScreen),
      );
    },
  };

  return {
    state: optimisticAudience,
    threadActions,
    projectorActions,
    pending,
  };
}
